import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Plan } from "../../types";
import { claimLinkPayment } from "../../utils/api";
import { Banner, Icon } from "../common/ui";

/**
 * Shown after the bePaid payment page opened in another window. A product link
 * does not tell us who paid, so the customer confirms here («Я оплатил(а)») and
 * the team checks the money and opens access — this sheet says exactly that.
 */
export default function LinkPaySheet({
  plan, paymentId, token, failed, onClose,
}: {
  plan: Plan;
  paymentId: string | null;       // null while the start request is in flight
  token: string;
  failed: boolean;                // the start request failed (link still opened)
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const isRu = !i18n.language?.startsWith("en");
  const [state, setState] = useState<"waiting" | "sending" | "claimed" | "error">("waiting");

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const claim = async () => {
    if (!paymentId) return;
    setState("sending");
    try {
      await claimLinkPayment(paymentId, token);
      setState("claimed");
    } catch {
      setState("error");
    }
  };

  const title = (isRu ? plan.title_ru : plan.title_en) || plan.title_ru;
  const price = Number(plan.price).toLocaleString("ru-RU", { maximumFractionDigits: 0 });

  return (
    <div className="sheet-backdrop" role="presentation" onClick={onClose}>
      <div className="sheet glass glass-lg" role="dialog" aria-modal="true" aria-labelledby="linkpay-title" onClick={(e) => e.stopPropagation()}>
        <div className="between" style={{ marginBottom: 12 }}>
          <p className="eyebrow" style={{ margin: 0 }}>{t("linkPay.eyebrow")}</p>
          <button className="icon-btn" onClick={onClose} aria-label={t("report.close")} style={{ width: 36, height: 36 }}>
            <Icon name="x" size={18} />
          </button>
        </div>

        {state === "claimed" ? (
          <div className="checkout-done">
            <span className="checkout-done__icon is-pending"><Icon name="clock" size={30} /></span>
            <h2 className="t-title" id="linkpay-title">{t("linkPay.claimedTitle")}</h2>
            <p className="t-sm muted" style={{ margin: 0 }}>{t("linkPay.claimedText")}</p>
            <button className="btn btn-primary btn-block" onClick={onClose} style={{ marginTop: 6 }}>{t("pay.doneButton")}</button>
          </div>
        ) : (
          <>
            <h2 className="t-title" id="linkpay-title" style={{ margin: "0 0 4px" }}>{title}</h2>
            <p className="checkout-price" style={{ margin: "0 0 12px" }}>
              <span className="checkout-price__value">{price} ₽</span>
              <span className="t-xs muted-2">{t("linkPay.viaBepaid")}</span>
            </p>
            <ol className="linkpay-steps">
              <li>{t("linkPay.step1")}</li>
              <li>{t("linkPay.step2")}</li>
              <li>{t("linkPay.step3")}</li>
            </ol>
            {failed && <div style={{ marginTop: 10 }}><Banner kind="error">{t("linkPay.startFailed")}</Banner></div>}
            {state === "error" && <div style={{ marginTop: 10 }}><Banner kind="error">{t("linkPay.claimFailed")}</Banner></div>}
            <button className="btn btn-primary btn-block" onClick={claim} disabled={!paymentId || state === "sending"} style={{ marginTop: 14 }}>
              {state === "sending" ? <span className="spinner" /> : <><Icon name="check" size={17} /> {t("linkPay.claim")}</>}
            </button>
            <button className="btn btn-ghost btn-block" onClick={onClose} style={{ marginTop: 6 }}>{t("linkPay.later")}</button>
          </>
        )}
      </div>
    </div>
  );
}
